import { createContext, useContext } from "react";

/** Mirrors the runtime config the CDK Frontend construct writes to config.json. */
export interface RuntimeConfig {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  cognitoDomain: string;
  apiBaseUrl: string;
  appUrl: string;
  identityProvider: string;
  /** LaunchDarkly client-side ID for the React SDK (public). Empty = flags off/fallback. */
  ldClientId: string;
}

/**
 * Load config at startup. In production the CDK deploy writes /config.json to
 * the bucket. In local dev there's no such file, so fall back to VITE_* env vars
 * (see .env.example) pointing at the deployed backend.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (res.ok) return (await res.json()) as RuntimeConfig;
  } catch {
    /* fall through to env */
  }
  const env = import.meta.env;
  return {
    region: env.VITE_REGION ?? "us-east-1",
    userPoolId: env.VITE_USER_POOL_ID ?? "",
    userPoolClientId: env.VITE_USER_POOL_CLIENT_ID ?? "",
    cognitoDomain: env.VITE_COGNITO_DOMAIN ?? "",
    apiBaseUrl: env.VITE_API_BASE_URL ?? "",
    appUrl: window.location.origin,
    identityProvider: env.VITE_IDENTITY_PROVIDER ?? "Google",
    ldClientId: env.VITE_LD_CLIENT_ID ?? "",
  };
}

export const ConfigContext = createContext<RuntimeConfig | null>(null);

export function useConfig(): RuntimeConfig {
  const config = useContext(ConfigContext);
  if (!config) throw new Error("useConfig must be used within ConfigContext");
  return config;
}
